import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Badge,
  Container,
  Avatar,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  ShoppingCart,
  AccountCircle,
  Storefront,
  Receipt,
  Logout,
  Security,
  Favorite,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { colors, TENANT_LOGO, TENANT_NAME } from '../utils/constants';
import { ImpersonationBanner } from './ImpersonationBanner';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin, isImpersonating, impersonatedUser, stopImpersonation } = useAuth();
  const { cart } = useCart();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // Calculate cart item count using the correct cart structure
  const cartItemCount = cart?.items?.reduce((total: number, item: any) => {
    // Each cart item has numberOfCases and caseQuantity
    return total + (item.numberOfCases || 0);
  }, 0) || 0;

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
    navigate('/login');
  };

  const isActiveRoute = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleMobileMenuClose();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'grey.50', width: '100%', maxWidth: '100vw', overflowX: 'hidden', position: 'relative' }}>
      {isImpersonating && impersonatedUser && (
        <ImpersonationBanner
          impersonatedUserName={`${impersonatedUser.first_name || ''} ${impersonatedUser.last_name || ''}`.trim()}
          impersonatedUserEmail={impersonatedUser.email}
          onExit={stopImpersonation}
        />
      )}
      <AppBar
        position="static"
        sx={{
          backgroundColor: '#fff',
          color: colors.primary,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          width: '100%',
        }}
      >
        <Toolbar sx={{ position: 'relative', width: '100%', px: { xs: 1, sm: 2 } }}>
          {/* Mobile: Left side - Cart and Avatar */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 0.5, position: 'absolute', left: 8 }}>
            {/* Cart Icon */}
            <IconButton
              color="inherit"
              onClick={() => navigate('/cart')}
              sx={{ 
                color: 'text.secondary',
                flexShrink: 0,
              }}
            >
              <Badge badgeContent={cartItemCount} color="secondary">
                <ShoppingCart />
              </Badge>
            </IconButton>

            {/* User Menu */}
            <Box sx={{ flexShrink: 0 }}>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
                sx={{ 
                  p: 0.5,
                }}
              >
                <Avatar 
                  sx={{ 
                    width: 28, 
                    height: 28, 
                    bgcolor: `${colors.primary}20`,
                    color: colors.primary,
                    fontSize: '0.75rem'
                  }}
                >
                  {user?.first_name?.[0] || user?.email?.[0]?.toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                keepMounted
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem onClick={() => { navigate('/profile'); handleClose(); }}>
                  <AccountCircle sx={{ mr: 1 }} />
                  Profile
                </MenuItem>
                {isAdmin && (
                  <MenuItem onClick={() => { navigate('/admin'); handleClose(); }}>
                    <Security sx={{ mr: 1 }} />
                    Admin
                  </MenuItem>
                )}
                <MenuItem onClick={handleLogout}>
                  <Logout sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </Box>

          {/* Mobile: Center - Logo */}
          <Box 
            sx={{ 
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center', 
              justifyContent: 'center',
              cursor: 'pointer',
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 'auto',
            }}
            onClick={() => navigate('/')}
          >
            <img 
              src={TENANT_LOGO} 
              alt={TENANT_NAME} 
              style={{ height: 40, width: 40 }} 
            />
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                fontWeight: 700,
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                letterSpacing: '-0.5px',
                color: colors.primary,
                display: { xs: 'none', sm: 'block' },
                ml: { xs: 0, sm: 1 },
              }}
            >
            </Typography>
          </Box>

          {/* Mobile: Right side - Hamburger Menu */}
          <IconButton
            color="inherit"
            onClick={handleMobileMenuToggle}
            sx={{ 
              display: { xs: 'flex', md: 'none' },
              position: 'absolute',
              right: 8,
              color: 'text.secondary'
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Desktop: Left side - Logo */}
          <Box 
            sx={{ 
              display: { xs: 'none', md: 'flex' }, 
              alignItems: 'center', 
              cursor: 'pointer',
              mr: 3,
              flexShrink: 0,
            }}
            onClick={() => navigate('/')}
          >
            <img 
              src={TENANT_LOGO} 
              alt={TENANT_NAME} 
              style={{ height: 40, width: 40, marginRight: 12 }} 
            />
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                fontWeight: 700,
                fontSize: '1.5rem',
                letterSpacing: '-0.5px',
                color: colors.primary,
              }}
            >
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, ml: 'auto', mr: 2 }}>
            <Button
              color="inherit"
              startIcon={<Storefront />}
              onClick={() => navigate('/')}
              sx={{
                color: location.pathname === '/' ? colors.primary : 'text.primary',
                fontWeight: location.pathname === '/' ? 600 : 400,
                '&:hover': { backgroundColor: `${colors.primary}10` },
              }}
            >
              Products
            </Button>
            <Button
              color="inherit"
              startIcon={<Receipt />}
              onClick={() => navigate('/orders')}
              sx={{
                color: isActiveRoute('/orders') ? colors.primary : 'text.primary',
                fontWeight: isActiveRoute('/orders') ? 600 : 400,
                '&:hover': { backgroundColor: `${colors.primary}10` },
              }}
            >
              Orders
            </Button>
            <Button
              color="inherit"
              startIcon={<Favorite />}
              onClick={() => navigate('/favorites')}
              sx={{
                color: isActiveRoute('/favorites') ? colors.primary : 'text.primary',
                fontWeight: isActiveRoute('/favorites') ? 600 : 400,
                '&:hover': { backgroundColor: `${colors.primary}10` },
              }}
            >
              Favorites
            </Button>
            {isAdmin && (
              <Button
                color="inherit"
                startIcon={<Security />}
                onClick={() => navigate('/admin')}
                sx={{
                  color: isActiveRoute('/admin') ? colors.primary : 'text.primary',
                  fontWeight: isActiveRoute('/admin') ? 600 : 400,
                  '&:hover': { backgroundColor: `${colors.primary}10` },
                }}
              >
                Admin
              </Button>
            )}
          </Box>

          {/* Desktop: Right side - Cart and Avatar */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
            {/* Cart Icon */}
            <IconButton
              color="inherit"
              onClick={() => navigate('/cart')}
              sx={{ 
                mr: 1, 
                color: 'text.secondary',
                flexShrink: 0,
              }}
            >
              <Badge badgeContent={cartItemCount} color="secondary">
                <ShoppingCart />
              </Badge>
            </IconButton>

            {/* User Menu */}
            <Box sx={{ flexShrink: 0 }}>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
                sx={{ 
                  p: 1,
                }}
              >
                <Avatar 
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    bgcolor: `${colors.primary}20`,
                    color: colors.primary,
                    fontSize: '0.875rem'
                  }}
                >
                  {user?.first_name?.[0] || user?.email?.[0]?.toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                keepMounted
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem onClick={() => { navigate('/profile'); handleClose(); }}>
                  <AccountCircle sx={{ mr: 1 }} />
                  Profile
                </MenuItem>
                {isAdmin && (
                  <MenuItem onClick={() => { navigate('/admin'); handleClose(); }}>
                    <Security sx={{ mr: 1 }} />
                    Admin
                  </MenuItem>
                )}
                <MenuItem onClick={handleLogout}>
                  <Logout sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Toolbar>

        {/* Mobile Drawer */}
        <Drawer
          anchor="left"
          open={mobileMenuOpen}
          onClose={handleMobileMenuClose}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 280,
            },
          }}
        >
          <Box
            sx={{
              width: 280,
              pt: 2,
            }}
            role="presentation"
          >
            <Box sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <img 
                src="/sw-logo-pink-rgb-512.png" 
                alt="ShareWares" 
                style={{ height: 40, width: 40 }} 
              />
              <Typography variant="h6" sx={{ fontWeight: 700, color: colors.primary }}>
                ShareWares
              </Typography>
            </Box>
            <Divider />
            <List>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleNavigation('/')}
                  selected={location.pathname === '/'}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: `${colors.primary}10`,
                      borderLeft: `3px solid ${colors.primary}`,
                    },
                  }}
                >
                  <ListItemIcon>
                    <Storefront color={location.pathname === '/' ? 'primary' : 'action'} />
                  </ListItemIcon>
                  <ListItemText primary="Products" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleNavigation('/orders')}
                  selected={isActiveRoute('/orders')}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: `${colors.primary}10`,
                      borderLeft: `3px solid ${colors.primary}`,
                    },
                  }}
                >
                  <ListItemIcon>
                    <Receipt color={isActiveRoute('/orders') ? 'primary' : 'action'} />
                  </ListItemIcon>
                  <ListItemText primary="Orders" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleNavigation('/favorites')}
                  selected={isActiveRoute('/favorites')}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: `${colors.primary}10`,
                      borderLeft: `3px solid ${colors.primary}`,
                    },
                  }}
                >
                  <ListItemIcon>
                    <Favorite color={isActiveRoute('/favorites') ? 'primary' : 'action'} />
                  </ListItemIcon>
                  <ListItemText primary="Favorites" />
                </ListItemButton>
              </ListItem>
              {isAdmin && (
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigation('/admin')}
                    selected={isActiveRoute('/admin')}
                    sx={{
                      '&.Mui-selected': {
                        backgroundColor: `${colors.primary}10`,
                        borderLeft: `3px solid ${colors.primary}`,
                      },
                    }}
                  >
                    <ListItemIcon>
                      <Security color={isActiveRoute('/admin') ? 'primary' : 'action'} />
                    </ListItemIcon>
                    <ListItemText primary="Admin" />
                  </ListItemButton>
                </ListItem>
              )}
            </List>
            <Divider />
            <List>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { handleNavigation('/profile'); handleMobileMenuClose(); }}>
                  <ListItemIcon>
                    <AccountCircle />
                  </ListItemIcon>
                  <ListItemText primary="Profile" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { handleLogout(); handleMobileMenuClose(); }}>
                  <ListItemIcon>
                    <Logout />
                  </ListItemIcon>
                  <ListItemText primary="Logout" />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
        </Drawer>
      </AppBar>

      <Container 
        maxWidth="xl" 
        sx={{ 
          flex: 1, 
          py: { xs: 3, md: 5 },
          px: { xs: 2, md: 4 },
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        {children}
      </Container>
    </Box>
  );
};

export default Layout; 